<template>
  <ViewContainer class="px-8" data-tourid="ai-chat-view">
    <v-progress-linear
      indeterminate
      height="5"
      :active="isLoadingPastConversation"
      class="past-conversations-loading-bar"
    ></v-progress-linear>

    <div
      v-if="showLoading"
      class="loading-container d-flex justify-center align-center"
    >
      <v-progress-circular indeterminate color="primary" :size="100" :width="4">
        <v-icon color="blue lighten-1" size="40">mdi-creation-outline</v-icon>
      </v-progress-circular>
    </div>

    <div
      v-if="!showLoading"
      class="d-flex w-100"
      :class="showInitialState ? 'justify-end' : 'justify-space-between'"
    >
      <div v-if="!showInitialState" class="d-flex justify-center align-center">
        <v-icon class="mr-1 mt-1" color="primary" size="20px"
          >mdi-comment-plus-outline
        </v-icon>

        <Button
          plain
          :disabled="!hasTokensAvailable || hideNewConversation"
          :text="$t('ai_features.chat_view.create_new_conversation')"
          @click="createNewConversation"
        />
      </div>

      <div class="d-flex align-center">
        <v-switch
          v-if="isDevOrLocalEnv"
          v-model="shouldUseFakeResponses"
          dense
          label="Use fake responses"
          class="pa-0 mt-0 me-8"
          hide-details
        />

        <Button
          plain
          icon="mdi-chevron-right"
          iconPosition="right"
          :text="$t('ai_features.chat_view.past_conversations_button')"
          @click="showPastConversationsDrawer"
        />
      </div>
    </div>

    <div
      class="ai-chat-view-container"
      :class="
        `ai-chat-view-${showInitialState ? 'small-height' : 'full-height'}`
      "
    >
      <div
        v-if="!showLoading && showInitialState"
        class="d-flex flex-column my-4"
      >
        <div class="greetings-container">
          <span
            class="h6 mb-3"
            v-html="
              $t('ai_features.chat_view.title', {
                displayName,
              })
            "
          />
        </div>

        <div v-if="showPromptOptions" class="d-flex justify-center">
          <PromptItem
            v-for="button in promptsOptions"
            class="me-2 mb-2"
            :disabled="!hasTokensAvailable"
            :key="button.title"
            :item="button"
            @handlePromptInteraction="handlePromptInteraction"
          />
        </div>
      </div>

      <div class="chat-container h-100">
        <AIChat
          v-show="!showLoading && mountChat && hasTokensAvailable"
          ref="reverAIChat"
          :hasTokensAvailable="hasTokensAvailable"
          :pastConversationId="pastConversationId"
          :useFakeResponses="shouldUseFakeResponses"
          @hideLoading="hidePastConversationLoading"
          @insufficientTokens="handleInsufficientTokensFromChat"
          @isReady="handleIsAIChatReady"
          @negativeFeedbackReceived="handleNegativeFeedback"
          @positiveFeedbackReceived="handlePositiveFeedback"
          @userInteracted="handleUserInteracted"
        />

        <div
          v-if="!showLoading && !hasTokensAvailable"
          class="d-flex justify-center align-center"
        >
          <AiErrorAlert
            v-if="showErrorAlert"
            :errorType="aiErrorStatusCode"
            :textClass="['body-1']"
            :alertClass="['my-6']"
          />
        </div>

        <div
          v-if="false"
          class="d-flex align-center align-self-start blue-grey--text text--darken-1"
          style="margin-left: 64px;"
        >
          <v-icon class="me-1" color="blue-grey--darken-1" size="16"
            >mdi-information-outline</v-icon
          >
          <span
            v-html="$t('ai_features.help_center_article')"
            class="caption"
          />
        </div>

        <div
          v-if="!showLoading && !isLoadingPastConversation"
          class="d-flex justify-start align-center align-self-center text-center disclaimer-container"
        >
          <span
            v-html="$t('ai_features.disclaimer')"
            class="caption blue-grey--text disclaimer-text"
          />
        </div>
      </div>

      <PastConversationsDrawer
        v-if="viewPastConversationsDrawer"
        :visible="viewPastConversationsDrawer"
        :selectedConversationId="pastConversationId"
        @close="viewPastConversationsDrawer = false"
        @selectConversation="handleSelectConversation"
        @deleteConversation="handleDeleteConversation"
      />
      <DeleteConfirmationModal
        :show="showDeleteConfirmationModal"
        :isLoading="isDeletingConversation"
        :title="deleteTitleText"
        :text="deleteMessageText"
        @cancel="showDeleteConfirmationModal = false"
        @delete="deleteConversation"
      />

      <FeedbackDialog
        :shown="isFeedbackDialogShown"
        @send="handleFeedbackDialogSend"
        @close="handleFeedbackDialogClose"
      />
    </div>
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import config from '@/config'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import Button from '@/components/UI/Button.vue'
import AiErrorAlert from '@/components/AIManagement/AiErrorAlert.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import constants from './constants'
import AIChat from './AIChat.vue'
import PromptItem from './PromptItem.vue'
import PastConversationsDrawer from './PastConversationsDrawer.vue'
import FeedbackDialog from './FeedbackDialog.vue'

import api from '@/api'

export default {
  name: 'AIChatView',

  mixins: [mixin, trackerMixin],

  components: {
    AIChat,
    AiErrorAlert,
    Button,
    DeleteConfirmationModal,
    FeedbackDialog,
    PastConversationsDrawer,
    PromptItem,
    ViewContainer,
  },

  data() {
    return {
      hasTokensAvailable: false,
      hideNewConversation: false,
      mountChat: false,
      showErrorAlert: false,
      aiErrorStatusCode: 0,
      isDeepChatReady: false,
      showPromptOptions: false, // Change to true to show suggested prompts once our backend supports it (requires developing a mongo tool with specific query generation capabilities)
      showInitialState: true,
      viewPastConversationsDrawer: false,
      showDeleteConfirmationModal: false,
      conversationToDelete: {},
      isDeletingConversation: false,
      showLoading: true,
      isLoadingPastConversation: false,
      showAiChat: false,
      promptsOptions: [
        {
          title: this.$t('ai_features.intro_prompts.quick_action'),
          description: this.$t(
            'ai_features.intro_prompts.quick_action_description'
          ),
          type: this.$constants.exploreEntityTypes.quickAction,
          prompt: this.$constants.PROMPTS.quickAction,
        },
        {
          title: this.$t('ai_features.intro_prompts.improvement_idea'),
          description: this.$t(
            'ai_features.intro_prompts.improvement_idea_description'
          ),
          type: this.$constants.exploreEntityTypes.improvementIdea,
          prompt: this.$constants.PROMPTS.improvementIdea,
        },
        {
          title: this.$t('ai_features.intro_prompts.problem_solving'),
          description: this.$t(
            'ai_features.intro_prompts.problem_solving_description'
          ),
          type: this.$constants.exploreEntityTypes.problemSolving,
          prompt: this.$constants.PROMPTS.problemSolving,
        },
        {
          title: this.$t('ai_features.intro_prompts.task'),
          description: this.$t('ai_features.intro_prompts.task_description'),
          type: this.$constants.exploreEntityTypes.task,
          prompt: this.$constants.PROMPTS.task,
        },
        {
          title: this.$t('ai_features.intro_prompts.challenge'),
          description: this.$t(
            'ai_features.intro_prompts.challenge_description'
          ),
          type: this.$constants.exploreEntityTypes.challenge,
          prompt: this.$constants.PROMPTS.challenge,
        },
      ],
      pastConversationId: null,
      shouldUseFakeResponses: config.env === 'localhost',
      isFeedbackDialogShown: false,
      feedbackTrackingData: null,
    }
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    accountPhoto() {
      return this.account?.photo ?? null
    },

    displayName() {
      return this.account.displayname
    },

    deleteTitleText() {
      return this.$t('ai_features.chat_view.delete_conversation_title')
    },

    deleteMessageText() {
      return this.$t('ai_features.chat_view.delete_conversation_message', {
        conversationTitle: this.conversationToDelete.title,
      })
    },

    isDevOrLocalEnv() {
      return (
        config.reverEnv === 'uswest2-devtest01' || config.env === 'localhost'
      )
    },
  },

  mounted() {
    this.checkTokensAvailability()
  },

  methods: {
    handlePromptInteraction(text) {
      this.$refs.reverAIChat.handlePromptInteraction(text)
      this.showInitialState = false
    },

    handleUserInteracted() {
      this.showInitialState = false
    },

    showPastConversationsDrawer() {
      this.viewPastConversationsDrawer = true
    },

    async handleIsAIChatReady(value) {
      if (value) {
        this.isLoadingPastConversation = false
        this.showLoading = false
      }
    },

    hidePastConversationLoading() {
      this.isLoadingPastConversation = false
    },

    handleSelectConversation(conversation) {
      this.mountChat = true
      if (!this.pastConversationId) {
        this.isLoadingPastConversation = true
      }
      this.pastConversationId = conversation.id
      this.showErrorAlert = false
      this.viewPastConversationsDrawer = false
    },

    handleDeleteConversation(conversation) {
      this.showDeleteConfirmationModal = true
      this.conversationToDelete = conversation
    },

    async deleteConversation() {
      try {
        this.isDeletingConversation = true
        await api.ai.deleteChat(
          this.organizationId,
          this.conversationToDelete.id
        )
        this.isDeletingConversation = false

        this.viewPastConversationsDrawer = false
      } catch (error) {
        this.isDeletingConversation = false
      }
      this.showDeleteConfirmationModal = false
      this.conversationToDelete = {}
    },

    async checkTokensAvailability() {
      try {
        this.showErrorAlert = false
        this.hasTokensAvailable = false
        this.showLoading = true
        const response = await api.ai.getTokensAvailability(this.organizationId)
        if (response.data) {
          this.hasTokensAvailable = true
          this.mountChat = true
        } else {
          this.hasTokensAvailable = false
          this.showLoading = false
        }
      } catch (error) {
        this.hasTokensAvailable = false
        this.showErrorAlert = true
        this.aiErrorStatusCode = error.response?.status
        this.showLoading = false
      }
    },

    async createNewConversation() {
      await this.checkTokensAvailability()
      this.showInitialState = true
      this.pastConversationId = null

      if (this.hasTokensAvailable) {
        this.$refs.reverAIChat.clearMessages()
      }
      this.showLoading = false
    },

    handleInsufficientTokensFromChat() {
      this.hideNewConversation = true
    },

    handlePositiveFeedback(trackingData) {
      this.trackAIResponseLike(trackingData)
      this.showSuccess(this.$t('ai_features.message_feedback_received'))
      this.persistFeedbackInCache(trackingData.messageId)
    },

    handleNegativeFeedback(trackingData) {
      this.feedbackTrackingData = trackingData
      this.isFeedbackDialogShown = true
    },

    handleFeedbackDialogSend(feedbackComments) {
      this.trackAIResponseDislike({
        ...this.feedbackTrackingData,
        feedbackComments,
      })
      this.isFeedbackDialogShown = false
      this.showSuccess(this.$t('ai_features.message_feedback_received'))
      this.persistFeedbackInCache(this.feedbackTrackingData.messageId)
    },

    handleFeedbackDialogClose() {
      this.isFeedbackDialogShown = false
      this.feedbackTrackingData = null
    },

    persistFeedbackInCache(messageId) {
      window.localStorage.setItem(
        `${constants.FEEDBACK_CACHE_KEY_PREFIX}${messageId}`,
        'true'
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.loading-container {
  height: 90vh;
}

.chat-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.ai-chat-view {
  &-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

.ai-chat-view-full-height {
  height: 85vh;
}

.ai-chat-view-small-height {
  height: 45vh;
}

.greetings-container {
  max-width: 400px;
}

.past-conversations-loading-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

.disclaimer-container {
  max-width: 50vw;
}

.disclaimer-text a {
  color: $color-blue-grey !important;
}
</style>
